import React from "react";
import { Link } from "react-router-dom";
import { Salad, Sparkles, ClipboardList, MessageSquare, ChefHat, ChevronRight, CheckCircle2, ExternalLink, ListChecks } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Dlazdice } from "../../components/ui.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getViewAsRole } from "../../lib/auth.js";
import NotificationsPanel from "../../components/NotificationsPanel.jsx";

// Dashboard výživové specialistky (v2, 12.7.2026) — fronty práce:
// jídelníčky (vypracovat / schválit / žádosti / recepty klientek)
// a masterplán (žádosti o změnu + komentáře klientek).
// Komentáře se otevírají do NOVÉHO okna (ať neztratí rozpracovaný přehled).

// focus=<kotva> — detail klienta po otevření sroluje přímo na část k řešení
// (jid-sekce / jid-zadosti / jid-recepty / mp-zadosti / mp-komentare).
function clientHref(name, focus) {
  return `/clients?search=${encodeURIComponent(name || "")}&open=1${focus ? `&focus=${encodeURIComponent(focus)}` : ""}`;
}
function fmtCz(iso) {
  if (!iso) return "—";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${Number(p[2])}. ${Number(p[1])}.` : iso;
}
function initials(name) {
  const parts = String(name || "").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}
const AVATAR_BG = [
  "from-emerald-400 to-teal-500", "from-brand-400 to-brand-500",
  "from-sky-400 to-blue-500", "from-amber-400 to-orange-500", "from-rose-400 to-pink-500",
];
function avatarClass(name) {
  let h = 0;
  for (const ch of String(name || "")) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_BG[h % AVATAR_BG.length];
}

// Barevná témata karet — ikona v bublině, count pill, akcentní horní linka.
const THEMES = {
  violet:  { bubble: "bg-brand-100 text-brand-600",   pill: "bg-brand-100 text-brand-700",   line: "from-brand-400 to-brand-400" },
  emerald: { bubble: "bg-emerald-100 text-emerald-600", pill: "bg-emerald-100 text-emerald-700", line: "from-emerald-400 to-teal-400" },
  amber:   { bubble: "bg-amber-100 text-amber-600",     pill: "bg-amber-100 text-amber-700",     line: "from-amber-400 to-orange-400" },
  sky:     { bubble: "bg-sky-100 text-sky-600",         pill: "bg-sky-100 text-sky-700",         line: "from-sky-400 to-blue-400" },
  indigo:  { bubble: "bg-plum-100 text-plum-600",   pill: "bg-plum-100 text-plum-700",   line: "from-plum-400 to-brand-400" },
  slate:   { bubble: "bg-slate-100 text-slate-600",     pill: "bg-slate-200 text-slate-700",     line: "from-slate-300 to-slate-400" },
};

function Row({ r, sub, meta, newTab, onResolve, focus }) {
  const cls = "flex items-center gap-3 px-2.5 py-2.5 hover:bg-slate-50 rounded-xl transition-colors";
  const inner = (
    <>
      <span className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarClass(r.klient)} text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm demo-sensitive`}>
        {initials(r.klient)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-800 truncate demo-sensitive">{r.klient}</div>
        {sub && <div className="text-xs text-slate-500 demo-sensitive line-clamp-2 leading-snug">{sub(r)}</div>}
      </div>
      {meta && <span className="text-[11px] font-semibold text-slate-400 text-right shrink-0 tabular-nums whitespace-nowrap">{meta(r)}</span>}
      {onResolve && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onResolve(r); }}
          title="Označit jako přečtené/vyřízené, komentář z přehledu zmizí (archivuje se i notifikace ve zvonečku)"
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-600 hover:text-white border border-emerald-200 rounded-lg px-2 py-1 transition-colors"
        >
          ✓ Vyřízeno
        </button>
      )}
      {newTab
        ? <ExternalLink className="w-4 h-4 text-slate-300 shrink-0" />
        : <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
    </>
  );
  // Vše → nové okno, ať specialistka neztratí rozjetý dashboard; &focus=
  // sroluje detail přímo na část k řešení. Owner náhled: nová karta by jinak
  // náhled ztratila — ?viewas ho přenese (reálná nutritionist má roli v localStorage).
  const va = getViewAsRole();
  const href = clientHref(r.klient, focus) + (va ? `&viewas=${encodeURIComponent(va)}` : "");
  return newTab ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
  ) : (
    <Link to={href} className={cls}>{inner}</Link>
  );
}

function QueueCard({ icon: Icon, theme = "slate", title, hint, rows, sub, meta, newTab = false, onResolve = null, focus = "", empty = "Nic k řešení" }) {
  const t = THEMES[theme] || THEMES.slate;
  const count = rows?.length || 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
      <div className={`h-1 bg-gradient-to-r ${t.line}`} />
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2.5 mb-1">
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${t.bubble}`}>
            <Icon className="w-4.5 h-4.5 w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-slate-900 truncate leading-tight">{title}</h3>
            {hint && <div className="text-[11px] text-slate-400 truncate">{hint}</div>}
          </div>
          <span className={`text-sm font-bold tabular-nums px-2.5 py-0.5 rounded-full ${count > 0 ? t.pill : "bg-slate-100 text-slate-400"}`}>{count}</span>
        </div>
        {count === 0 ? (
          <div className="text-[13px] text-slate-400 py-6 text-center flex items-center justify-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {empty}
          </div>
        ) : (
          <div className="mt-1.5 -mx-1 max-h-80 overflow-y-auto divide-y divide-slate-50">
            {rows.map((r, i) => (
              <Row key={`${r.id || "x"}-${i}`} r={r} sub={sub} meta={meta} newTab={newTab} onResolve={onResolve} focus={focus} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 13.9.2026 (fáze 4h): dlaždice a nadpisy sekcí ze vzorníku. Dřív měla každá
// dlaždice barevnou bublinu s ikonou a nadpisy sekcí byly jen drobná šedá
// velká písmena.
const TON_DLAZDICE = { violet: "znacka", emerald: "ok", amber: "zlata", sky: "info", slate: "neutralni" };

function StatTile({ theme, label, value }) {
  return <Dlazdice ton={TON_DLAZDICE[theme] || "neutralni"} popisek={label} hodnota={value} />;
}

function SectionTitle({ ikona, children }) {
  return (
    <div className="-mx-4 flex items-center gap-2 bg-brand-light px-4 py-2 sm:-mx-5 sm:px-5">
      {ikona ? React.createElement(ikona, { className: "h-3.5 w-3.5 shrink-0 text-brand-deep" }) : null}
      <span className="text-xs font-bold uppercase tracking-wide text-brand-950">{children}</span>
    </div>
  );
}

export default function NutriDashboard() {
  const { data } = useApiData("/api/nutri-dashboard");
  const d = data || {};
  // Vyřízené komentáře — optimisticky zmizí hned, server archivuje notifikaci
  // (sdílený stav se zvonečkem), takže po refreshi už nepřijdou.
  const [vyrizeneKom, setVyrizeneKom] = React.useState(() => new Set());
  const komentarVyrizen = (r) => {
    setVyrizeneKom((prev) => { const n = new Set(prev); n.add(r.notifId); return n; });
    apiFetch("/api/nutri-dashboard", {
      method: "POST",
      body: JSON.stringify({ action: "komentar-vyrizen", klient_id: r.id, datum: r.datum }),
    }).catch(() => {});
  };
  const komentare = (d.masterplanKomentare || []).filter((k) => !vyrizeneKom.has(k.notifId));
  const n = (arr) => (data ? (arr?.length || 0) : "—");
  const zadostiCelkem = data ? (d.jidelnicekZadosti?.length || 0) + (d.masterplanZadosti?.length || 0) : "—";

  return (
    <Stranka>
      <GradientHeader
        title="Výživa"
        napoveda="Jídelníčky na míru a masterplány na jednom místě. Klepnutí na klientku otevře její detail v novém okně."
        icon={Salad}
      />

      {/* Přehled čísel */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <StatTile theme="violet" label="Dotazníky k vypracování" value={n(d.jidelnicekVypracovat)} />
        <StatTile theme="emerald" label="Návrhy ke schválení" value={n(d.jidelnicekSchvalit)} />
        <StatTile theme="amber" label="Otevřené žádosti" value={zadostiCelkem} />
        <StatTile theme="sky" label="Komentáře za 7 dní" value={data ? komentare.length : "—"} />
      </div>

      {/* Jídelníčky */}
      <SectionTitle ikona={Salad}>Jídelníčky na míru</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueCard icon={Sparkles} theme="violet"
          title="Vypracovat jídelníček" hint="klientka vyplnila dotazník, čeká na první návrh, otevře se v novém okně"
          rows={d.jidelnicekVypracovat} sub={(r) => r.trener || "—"}
          meta={(r) => `dotazník ${fmtCz(r.vyplneno)}`} newTab focus="jid-sekce"
          empty="Žádný nový dotazník" />
        <QueueCard icon={Salad} theme="emerald"
          title="Zkontrolovat a zveřejnit" hint="AI návrh je hotový, čeká na vaše schválení, otevře se v novém okně"
          rows={d.jidelnicekSchvalit} sub={(r) => r.trener || "—"}
          meta={() => "návrh čeká"} newTab focus="jid-sekce"
          empty="Žádný návrh nečeká" />
        <QueueCard icon={MessageSquare} theme="amber"
          title="Žádosti o úpravu jídelníčku" hint="klientka chce jinou variantu nebo změnu, otevře se v novém okně"
          rows={d.jidelnicekZadosti} sub={(r) => r.text}
          meta={(r) => fmtCz(r.created_at)} newTab focus="jid-zadosti"
          empty="Žádné otevřené žádosti" />
        <QueueCard icon={ChefHat} theme="sky"
          title="Recepty klientek ke schválení" hint="po schválení se přidají do databáze receptů, otevře se v novém okně"
          rows={d.receptyKeSchvaleni} sub={(r) => `${r.nazev}${r.chod ? `, ${r.chod}` : ""}`}
          meta={(r) => fmtCz(r.created_at)} newTab focus="jid-recepty"
          empty="Žádný recept nečeká" />
      </div>

      {/* Masterplán */}
      <SectionTitle ikona={ListChecks}>Masterplán</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueCard icon={ClipboardList} theme="indigo"
          title="Žádosti o změnu masterplánu" hint="klientka chce upravit své úkoly, otevře se v novém okně"
          rows={d.masterplanZadosti} sub={(r) => r.text}
          meta={(r) => fmtCz(r.created_at)} newTab focus="mp-zadosti"
          empty="Žádné otevřené žádosti" />
        <QueueCard icon={MessageSquare} theme="slate"
          title="Komentáře klientek" hint="posledních 7 dní, otevírá se v novém okně"
          rows={komentare} sub={(r) => r.text}
          meta={(r) => fmtCz(r.datum)} newTab onResolve={komentarVyrizen} focus="mp-komentare"
          empty="Žádné nové komentáře" />
      </div>

      {/* Notifikace — úplně dole (14.7.2026 na přání ownera). Jen výživové
          typy; systémové chyby sem nepatří (filtruje NotificationsPanel). */}
      <NotificationsPanel />
    </Stranka>
  );
}
